import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table, Input, Select, Card, Space, Tag, Tooltip, Button, Drawer, Typography, Flex} from "antd";
import dayjs from "dayjs";
import { getActivitiesApi } from "../../apiservice/setting";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const typeColor = {
    c: "green",
    u: "blue",
    d: "red",
};

export default function ActivitiesPage() {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [type, setType] = useState(undefined);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [helpOpen, setHelpOpen] = useState(false);

    const loadActivities = async () => {
        setLoading(true);

        try {
            const res = await getActivitiesApi({
            page,
            limit: pageSize,
            search,
            type,
            });

            setActivities(res?.data.data || []);
            setTotal(res?.data.total || 0);
            console.log(res?.data.data)
        } catch (err) {
            console.error("Failed to load activities", err);
        } finally {
            setLoading(false);
        }
        };

    useEffect(() => {
        loadActivities();
    }, [page, pageSize, search, type]);

    const getRecordEditPath = (row) => {
        const name = row.activity_name?.toLowerCase();

        if (!name || !row.record_id) return null;
        
        if (name.includes("sale return")) {
            return `/sales/quick-sales-return-voucher/${row.record_id}`;
        }

        if (name.includes("sales order")) {
            return `/order/quick-sales-order-voucher/${row.record_id}`;
        }

        if (name.includes("sales quotation")) {
            return `/quotation/quick-quotation-voucher/${row.record_id}`;
        }

        if (name.includes("sale")) {
            return `/sales/quick-sales-voucher/${row.record_id}`;
        }

        if (name.includes("purchase return")) {
            return `/purchase/quick-purchase-return-voucher/${row.record_id}`;
        }               

        if (name.includes("purchase order")) {
            return `/order/quick-purchase-order-voucher/${row.record_id}`;
        }               

        if (name.includes("purchase")) {
            return `/purchase/quick-purchase-voucher/${row.record_id}`;
        } 

        if (name.includes("salary payment")) {
            return `/hrpayroll/salary-payment-voucher/${row.pay_id}`;
        }

        if (name.includes("manufacturing")) {
            return `/manufacturing-quick-voucher/${row.record_id}`;
        }

        if (name.includes("service expense")) {
            return `/service/quick-service-expense-voucher/${row.record_id}`;
        }

        if (name.includes("service")) {
            return `/service/quick-service-voucher/${row.record_id}`;
        }

        if (name.includes("tag field")) {
            return `/settings/tags`;
        }

        if (name.includes("tag group")) {
            return `/settings/tags`;
        }

        if (name.includes("recognition")) {
            return `/accounts/expense-recognitions`;
        }

        if (name.includes("journal")) {
            return `/accounts/journal-entry`;
        }

        if (name.includes("debtor receipt deleted")) {
            return `/accounts/debitor-receipts`;
        }

        if (name.includes("debtor receipt")) {
            return `/accounts/debitor-receipts/view/${row.record_id}`;
        }        

        if (name.includes("creditor/supplier payment")) {
            return `/accounts/creditor-payments`;
        }

        if (name.includes("company profile")) {
            return `/settings/institution-profile`;
        }
        
        if (name.includes("contra")) {
            return `/accounts/contra-entry`;
        }
        
        if (name.includes("expense")) {
            return `/accounts/expenses`;
        }
        
        if (name.includes("income")) {
            return `/accounts/income`;
        }

        if (name.includes("branch transaction")) {
            return `/accounts/branch-tran`;
        }

        if (name.includes("branch")) {
            return `/settings/branch-manage`;
        }

        if (name.includes("warehouse")) {
            return `/settings/warehouses`;
        }

        if (name.includes("password reset")) {
            return `/settings/users`;
        }

        if (name.includes("conversation")) {
            return `/crm/customers`;
        }
        
        if (name.includes("item category")) {
            return `/inventory/category`;
        } 
        
        if (name.includes("item model")) {
            return `/inventory/models-manage`;
        } 
        
        

        if (name.includes("job")) {
            return `/ats/jobs/${row.record_id}/view`;
        }

        if (name.includes("candidate")) {
            return `/ats/candidates/${row.record_id}/view`;
        }

        if (name.includes("application")) {
            return `/ats/applications/${row.record_id}/view`;
        }        
        

        return null;
    };


    const columns = [
        {
            title: "Activity",
            dataIndex: "activity_name",
            key: "activity_name",
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 100,
            render: (t) => <Tag color={typeColor[t]}>{t.toUpperCase()}</Tag>,
        },
        {
            title: "Record",
            key: "record",
            width: 240,
            render: (_, row) => {
                const path = getRecordEditPath(row);           
                if (!path) return "-";

                const name = row.activity_name?.toLowerCase();

                let label = "Record";
                let voucher = "-"; // fallback

                if (name.includes("sale return")) {
                label = "Sale Return";
                voucher = row.sale_r_voucher_no;
                } else if (name.includes("purchase return")) {
                label = "Purchase Return";
                voucher = row.pur_r_voucher_no;
                } else if (name.includes("purchase order")) {
                label = "Purchase Order";
                voucher = row.pur_order_no;
                } else if (name.includes("purchase")) {
                label = "Purchase";
                voucher = row.pur_voucher_no;
                } else if (name.includes("sales order")) {
                label = "Sales Order";
                voucher = row.sales_order_no;
                } else if (name.includes("sales quotation ")) {
                label = "Sales Quotation";
                voucher = row.quotation_no;
                } else if (name.includes("sale")) {
                label = "Sale";
                voucher = row.sale_voucher_no;
                } else if (name.includes("manufacturing")) {
                label = "Manufacturing";
                voucher = row.mf_voucher_no;
                } else if (name.includes("service expense")) {
                label = "Service Expense";
                voucher = row.service_ex_voucher_no;
                } else if (name.includes("service")) {
                label = "Service";
                voucher = row.service_voucher_no;
                } else if (name.includes("employee")){ 
                label = "Employee";
                }else if (name.includes("account")){
                label = "Account";
                }else if (name.includes("recognition")) {
                label = "Expense Recognition";
                voucher = ` amount ${row.recognition_amount}`;
                }else if (name.includes("journal")) {
                label = "Journal";
                voucher = row.journal_no;
                }else if (name.includes("debtor receipt")) {
                label = "Debtor Receipt";
                voucher = row.receipt_no;                
                }else if (name.includes("creditor/supplier payment")) {
                label = "Creditor/supplier payment";
                voucher = row.pay_no;
                }else if (name.includes("company profile")) {
                label = "company profile";
                voucher = row.company_name;
                }else if (name.includes("expense")) {
                label = "expense";
                voucher = row.expense_no;
                }else if (name.includes("income")) {
                label = "income";
                voucher = row.income_no;
                }else if (name.includes("password reset")) {
                label = "User";
                }else if (name.includes("conversation")) {
                label = "Conversation";
                }else if (name.includes("branch transaction")) {
                label = "branch transaction";
                voucher = row.branch_name;
                }else if (name.includes("branch")) {
                label = "branch";
                voucher = row.branch_name;
                }else if (name.includes("contra")) {
                label = "contra"; 
                voucher = row.contra_code;               
                }else if (name.includes("warehouse")) {
                label = "warehouse";
                voucher = row.warehouse_name;
                }else if (name.includes("tag field")) {
                label = "tag field";
                voucher = row.tag_field_name;
                }else if (name.includes("tag group")) {
                label = "tag group";
                voucher = row.tag_group_name;
                }else if (name.includes("item category")) {
                label = "category";
                voucher = row.category_name;
                }else if (name.includes("batch")) {
                label = "batch";
                voucher = row.batch_name;
                }else if (name.includes("item model")) {
                label = "model";
                voucher = row.model_name;
                }else if (name.includes("salary payment")) {
                    label = "Salary Payment";
                    voucher = row.salary_pay_code;
                }

                return (
                    <Link to={path}>
                        {voucher ? `${label} #${voucher}` : label}
                    </Link>
                );
            },
        },
        {
            title: "Created By",
            dataIndex: "full_name",
            key: "activity_created_by",
        },
        {
            title: "Date",
            dataIndex: "created_date",
            key: "created_date",
            width: 180,
            render: (d) => dayjs(d).format("DD MMM YYYY HH:mm"),
        },
    ];

    const handleSearch = (value) => {
        setSearch(value);
        setPage(1);
    };


    return (
        <Card
            style={{ margin: "clamp(8px, 3vw, 20px)" }}
            title={
                <Flex justify="space-between" align="center">
                <Title level={4} style={{ margin: 0 }}>
                    Activities
                </Title>

                <Tooltip title="Help">
                    <Button
                    type="text"
                    icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
                    onClick={() => setHelpOpen(true)}
                    />
                </Tooltip>
                </Flex>
            }
            >
            {/* 🔍 Search Bar */}
            <Space wrap style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Search activity"
                    allowClear
                    onSearch={handleSearch}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: "min(250px, 100%)" }}
                />
                <Select
                    placeholder="Type"
                    allowClear
                    style={{ width: 140 }}
                    onChange={(val) => {
                        setType(val);
                        setPage(1);
                    }}
                >
                    <Option value="c">Create</Option>
                    <Option value="u">Update</Option>
                    <Option value="d">Delete</Option>
                </Select>
            </Space>


            {/* 📋 Table */}
            <Table
                rowKey="activity_id"
                loading={loading}
                columns={columns}
                dataSource={activities}
                scroll={{ x: "max-content" }}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (newPage, newPageSize) => {
                        if (newPageSize !== pageSize) {
                        setPage(1);              // reset page
                        setPageSize(newPageSize);
                        } else {
                        setPage(newPage);
                        }
                    }
                    }}
            />
            <Drawer
                title="Activity Logs Help"
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                width={430}
                >
                <div style={{ lineHeight: 1.8 }}>
                    <h2
                    style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        marginBottom: "12px",
                        marginLeft: "12px"
                    }}
                    >
                    Activity Logs Management
                    </h2>

                    <p style={{ color: "#555", marginBottom: "16px",marginLeft:17 }}>
                    This section helps monitor system activities, user actions,
                    record updates, and workflow tracking across different ERP modules.
                    </p>

                    <div
                    style={{
                        background: "#f5f5f5",
                        padding: "14px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                    }}
                    >
                    <strong>What You Can Do</strong>

                    <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
                        <li>View system-wide activity history</li>
                        <li>Track Create, Update, and Delete actions</li>
                        <li>Search activities using keywords</li>
                        <li>Filter activities by action type</li>
                        <li>Open related records directly from activity logs</li>
                        <li>Track user activity and record ownership</li>
                        <li>View activity dates and timestamps</li>
                        <li>Monitor changes across CRM, Accounts, Inventory, HR, ATS, and other modules</li>
                        <li>Review workflow updates and transaction history</li>
                    </ol>
                    </div>

                    <div
                    style={{
                        background: "#fafafa",
                        border: "1px solid #eee",
                        padding: "14px",
                        borderRadius: "8px",
                    }}
                    >
                    <strong>Important Notes</strong>

                    <ul
                        style={{
                        paddingLeft: "18px",
                        marginTop: "10px",
                        color: "#666",
                        }}
                    >
                        <li>Create activities are shown in green.</li>
                        <li>Update activities are shown in blue.</li>
                        <li>Delete activities are shown in red.</li>
                        <li>Activity logs help monitor user operations and data changes.</li>
                        <li>Some activity records include direct navigation links.</li>
                        <li>Search and filters help quickly locate specific activities.</li>
                        <li>Activity tracking improves auditing and workflow monitoring.</li>
                    </ul>
                    </div>
                </div>
                </Drawer>
        </Card>
    );
}